import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { AdActivityDataType } from '@/api/ad';
import KeywordTableActionContent from '@/app/paid-search/KeywordTableActionContent';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import type SolAPIError from '@/error/SolAPIError';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';

type Props = {
  columns: InstantSearchDataTableColumnDef<AdActivityDataType>[];
  selectedAds: AdActivityDataType[];
  isMaxCompareLimit: boolean;
  data: AdActivityDataType[] | undefined;
  totalResults: number | undefined;
  isLoading: boolean;
  error: SolAPIError | null;
  menuSelectorOpen: boolean;
  advancedFiltersOpen: boolean;
  onMetricSelectorOpen: (open: boolean) => void;
  onAdvancedFiltersOpen: (open: boolean) => void;
  onSelectedAdsChange: (ads: AdActivityDataType[]) => void;
  onScoreAds: (adIds: string[]) => void;
};

const PaidSearchKeywordActivityTable = ({
  columns,
  selectedAds,
  isMaxCompareLimit,
  data,
  totalResults,
  isLoading,
  error,
  menuSelectorOpen,
  advancedFiltersOpen,
  onMetricSelectorOpen,
  onAdvancedFiltersOpen,
  onSelectedAdsChange,
  onScoreAds,
}: Props) => {
  const selectedAdIds = useMemo(() => selectedAds.map((ad) => ad.ad.id), [selectedAds]);
  const selectedAdById = useMemo(
    () => new Map(selectedAds.map((ad) => [ad.ad.id, ad])),
    [selectedAds],
  );
  const visibleAdById = useMemo(() => new Map((data ?? []).map((ad) => [ad.ad.id, ad])), [data]);

  const handleSelectRow = (ad: AdActivityDataType) => {
    const isAdSelected = selectedAdIds.includes(ad.ad.id);

    if (!isAdSelected && isMaxCompareLimit) {
      return;
    }

    if (isAdSelected) {
      onSelectedAdsChange(selectedAds.filter((a) => a.ad.id !== ad.ad.id));
      return;
    }

    onSelectedAdsChange([...selectedAds, ad]);
  };

  const handleSelectedAdIdsChange = (selectedRowIds: string[]) => {
    const nextSelectedAds = selectedRowIds.flatMap((adId) => {
      const existingAd = selectedAdById.get(adId);
      if (existingAd != null) {
        return [existingAd];
      }

      const visibleAd = visibleAdById.get(adId);
      return visibleAd != null ? [visibleAd] : [];
    });

    onSelectedAdsChange(nextSelectedAds);
  };

  const handleRemoveSelectedAd = (ad: AdActivityDataType) => {
    onSelectedAdsChange(selectedAds.filter((a) => a.ad.id !== ad.ad.id));
  };

  return (
    <>
      <InstantSearchDataTable<AdActivityDataType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={<Trans>No keywords found.</Trans>}
        error={error}
        getRowId={(record) => record.ad.id}
        initialColumnPinning={{
          start: ['adId'],
        }}
        rowSelection={{
          selectedRowIds: selectedAdIds,
          onSelectedRowIdsChange: handleSelectedAdIdsChange,
          getRowCanSelect: (row) =>
            selectedAdIds.includes(row.original.ad.id) || !isMaxCompareLimit,
          enableSelectAll: true,
          columnWidth: 40,
        }}
        actionContent={
          <KeywordTableActionContent
            selectedAds={selectedAds}
            onRemoveSelectedAd={handleRemoveSelectedAd}
            onScoreAds={onScoreAds}
          />
        }
        onRow={(record) => ({ onClick: () => handleSelectRow(record) })}
      />
      <DataTableMetricSelector
        open={menuSelectorOpen}
        onClose={() => onMetricSelectorOpen(false)}
      />
      <DataTableAdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={() => onAdvancedFiltersOpen(false)}
      />
    </>
  );
};

export default PaidSearchKeywordActivityTable;
